import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';
import type {
  FonteNotificacoes,
  NotificacaoCategoria,
  NotificacaoItem,
} from '@/types/notificationBell/types';

export const selectDropdownOpen = (state: RootState) => state.notificationBell.dropdownOpen;
export const selectActiveTab = (state: RootState) => state.notificationBell.activeTab;
export const selectCategoryFilter = (state: RootState) => state.notificationBell.categoryFilter;
export const selectLoading = (state: RootState) => state.notificationBell.loading;
export const selectError = (state: RootState) => state.notificationBell.error;

// Referência estável: um `[]` novo a cada chamada invalidaria a memoização dos seletores abaixo.
const SEM_ITENS: NotificacaoItem[] = [];

/**
 * Itens da caixa pedida. Vazio enquanto o estado guarda a outra caixa — entre a montagem de um
 * sino e o `pending` do fetch dele, o slice ainda tem os itens da tela anterior.
 */
const selectItensDaFonte = (state: RootState, fonte: FonteNotificacoes): NotificacaoItem[] =>
  state.notificationBell.fonte === fonte ? state.notificationBell.items : SEM_ITENS;

const byEntityType = (items: NotificacaoItem[], entityType?: string): NotificacaoItem[] => {
  if (!entityType) return items;
  return items.filter((item) => item.entityType === entityType);
};

export const selectUnreadCount = createSelector(
  [selectItensDaFonte],
  (items) => items.filter((i) => !i.lida).length,
);

export const selectUnreadCountByEntityType = createSelector(
  [
    selectItensDaFonte,
    (_state: RootState, _fonte: FonteNotificacoes, entityType?: string) => entityType,
  ],
  (items, entityType) => byEntityType(items, entityType).filter((i) => !i.lida).length,
);

export const selectFilteredItems = createSelector(
  [selectItensDaFonte, selectActiveTab, selectCategoryFilter],
  (items, tab, categoryFilter) => {
    const byTab = tab === 'unread' ? items.filter((i) => !i.lida) : items;
    if (!categoryFilter) return byTab;
    return byTab.filter((i) => i.categoria === categoryFilter);
  },
);

export const selectFilteredItemsByEntityType = createSelector(
  [
    selectItensDaFonte,
    selectActiveTab,
    selectCategoryFilter,
    (_state: RootState, _fonte: FonteNotificacoes, entityType?: string) => entityType,
  ],
  (items, tab, categoryFilter, entityType) => {
    const scoped = byEntityType(items, entityType);
    const byTab = tab === 'unread' ? scoped.filter((i) => !i.lida) : scoped;
    if (entityType || !categoryFilter) return byTab;
    return byTab.filter((i) => i.categoria === categoryFilter);
  },
);

export const selectAvailableCategories = createSelector(
  [selectItensDaFonte],
  (items): NotificacaoCategoria[] => {
    const seen = new Set<NotificacaoCategoria>();
    for (const i of items) seen.add(i.categoria);
    return Array.from(seen);
  },
);
